// "Klaviyo-idővonal" on the contact's page (7.2 M2): what Klaviyo saw this
// contact do — the events the flows in shadow read (product views, carts,
// checkouts, orders, the emails Klaviyo sent or skipped), newest first. Thin
// on purpose: the existing table and pager, no chart.
//
// The rows are the pull's copy (`klaviyo_event`), matched to this contact by
// address and country; the flow and message are Klaviyo's ids, the value is
// `$value` in the account's currency.

import { useEffect, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { ApiError, fetchContactKlaviyoEvents } from "@/lib/api";
import type { ContactKlaviyoEvent } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { minute } from "@/components/emc/format";
import { EmptyState, ErrorState } from "@/components/emc/states";

function problem(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

/** "36 380 HUF" from whole cents; "—" when the event carried no value. */
function amount(cents: number | null, currency: string | null): string {
  if (cents === null) return "—";
  const value = (cents / 100).toLocaleString("hu-HU", { maximumFractionDigits: 2 });
  return currency === null ? value : `${value} ${currency}`;
}

const NONE = <span className="text-muted-foreground">—</span>;

const COLUMNS: ColumnDef<ContactKlaviyoEvent>[] = [
  { id: "metric", header: "Esemény", cell: ({ row }) => row.original.metric },
  {
    id: "when",
    header: "Mikor",
    cell: ({ row }) => minute(row.original.occurred_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "flow",
    header: "Flow",
    cell: ({ row }) => row.original.flow_source_id ?? NONE,
    meta: { mono: true },
  },
  {
    id: "message",
    header: "Levél",
    cell: ({ row }) => row.original.message_source_id ?? NONE,
    meta: { mono: true },
  },
  {
    id: "value",
    header: "Érték",
    cell: ({ row }) => amount(row.original.value_cents, row.original.currency),
    meta: { mono: true },
  },
];

export function ContactKlaviyoEvents({ contactId }: { contactId: number }) {
  const [items, setItems] = useState<ContactKlaviyoEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load(from: string | null) {
    setLoading(true);
    fetchContactKlaviyoEvents(contactId, from)
      .then((body) => {
        setItems((rows) => (from === null ? body.items : [...rows, ...body.items]));
        setCursor(body.next_cursor);
        setError(null);
      })
      .catch((reason: unknown) => setError(problem(reason)))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(null), [contactId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Klaviyo-idővonal</CardTitle>
        <CardDescription>
          Amit a Klaviyo látott ettől a kontakttól — termék-megtekintés, kosár, pénztár, rendelés, a Klaviyo
          által küldött vagy kihagyott levelek —, a legfrissebb elöl. Ezekből dönt az árnyék-módú flow-motor.
          A flow és a levél a Klaviyo azonosítója; az érték a fiók pénznemében.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error !== null && <ErrorState message={error} />}
        {!loading && items.length === 0 && error === null ? (
          <EmptyState title="Ehhez a kontakthoz még nincs Klaviyo-esemény." />
        ) : (
          <DataTable
            label="Klaviyo-idővonal"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={COLUMNS}
            data={items}
            getRowId={(row) => String(row.id)}
            loading={loading && items.length === 0}
            footer={
              <TablePager
                summary={`${items.length} esemény${cursor === null && items.length > 0 ? " · ennyi van" : ""}`}
              >
                <Button
                  variant="outline"
                  type="button"
                  disabled={cursor === null || loading}
                  onClick={() => load(cursor)}
                >
                  Továbbiak
                </Button>
              </TablePager>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
